import { useEffect, useMemo, useState } from 'react';
import { HeatmapTable } from '../HeatmapTable';
import { DEFAULT_HEATMAP_VIEW_PRESET, getDisplayExpirations } from '../../lib/heatmapViewPreset';
import type { ExposureType, HeatmapData, Ticker } from '../../store/heatmapStore';

interface DiscordRenderPayload {
  ticker: Ticker;
  heatmaps: Partial<Record<ExposureType, HeatmapData>>;
  updatedAt: string;
}

interface DiscordHeatmapRenderProps {
  renderId: string;
}

type RenderState =
  | { status: 'loading' }
  | { status: 'ready'; payload: DiscordRenderPayload }
  | { status: 'error'; error: string };

interface RenderApiResponse {
  success: boolean;
  data?: DiscordRenderPayload;
  error?: string;
}

function isRenderApiResponse(value: unknown): value is RenderApiResponse {
  return (
    typeof value === 'object' &&
    value != null &&
    'success' in value &&
    typeof value.success === 'boolean'
  );
}

function formatEasternTime(timestamp: string): string {
  const date = new Date(timestamp);
  if (!Number.isFinite(date.getTime())) {
    return 'Unknown';
  }

  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);
}

function formatSpotPrice(heatmaps: DiscordRenderPayload['heatmaps']): string {
  const spotPrice = heatmaps.gex?.spotPrice ?? heatmaps.vex?.spotPrice;
  if (spotPrice == null) {
    return 'Spot unknown';
  }

  return `Spot ${spotPrice >= 100 ? spotPrice.toFixed(2) : spotPrice.toFixed(3)}`;
}

function ExposurePanel({ data, ticker }: { data: HeatmapData; ticker: Ticker }) {
  const visibleExpirations = useMemo(
    () =>
      getDisplayExpirations(data.expirations, DEFAULT_HEATMAP_VIEW_PRESET.visibleExpirationCount),
    [data.expirations],
  );

  return (
    <section className="flex min-h-0 flex-1 flex-col overflow-hidden border border-zinc-800 bg-[#0a0a0a]">
      <div className="flex h-10 shrink-0 items-center justify-between border-b border-zinc-800 px-4">
        <div className="font-mono text-[13px] font-semibold text-white">
          {data.type.toUpperCase()}
        </div>
        <div className="font-mono text-[11px] text-zinc-400">
          {data.expirations.length} expirations
        </div>
      </div>
      <div className="flex min-h-0 flex-1">
        <HeatmapTable
          rows={data.rows}
          expirations={visibleExpirations}
          spotPrice={data.spotPrice}
          visibleStrikeCount={36}
          type={data.type}
          ticker={ticker}
        />
      </div>
    </section>
  );
}

export function DiscordHeatmapRender({ renderId }: DiscordHeatmapRenderProps) {
  const [state, setState] = useState<RenderState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;

    fetch(`/api/internal/discord-render/${renderId}`)
      .then(async (response) => {
        const body: unknown = await response.json();
        if (
          !isRenderApiResponse(body) ||
          !response.ok ||
          body.success !== true ||
          body.data == null
        ) {
          throw new Error(
            isRenderApiResponse(body)
              ? (body.error ?? 'Render payload unavailable')
              : 'Render payload unavailable',
          );
        }
        return body.data;
      })
      .then((payload) => {
        if (!cancelled) {
          setState({ status: 'ready', payload });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setState({
            status: 'error',
            error: error instanceof Error ? error.message : 'Unknown error',
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [renderId]);

  if (state.status === 'loading') {
    return <div className="dark h-screen bg-background text-zinc-400">Loading...</div>;
  }

  if (state.status === 'error') {
    return (
      <div className="dark flex h-screen items-center justify-center bg-background font-mono text-red-300">
        {state.error}
      </div>
    );
  }

  const { payload } = state;

  return (
    <main className="dark bg-background p-4" data-discord-render-ready="true">
      <div
        className="flex h-[880px] w-[1368px] flex-col gap-3 overflow-hidden bg-background"
        data-discord-screenshot
      >
        <header className="flex h-16 shrink-0 items-center justify-between border border-zinc-800 bg-[#0d0d0d] px-5">
          <div>
            <div className="font-mono text-[22px] font-semibold tracking-normal text-white">
              {payload.ticker} GEX / VEX
            </div>
            <div className="font-mono text-[12px] text-zinc-400">
              {formatSpotPrice(payload.heatmaps)}
            </div>
          </div>
          <div className="text-right font-mono text-[12px] text-zinc-400">
            Updated {formatEasternTime(payload.updatedAt)} ET
          </div>
        </header>

        <div className="grid min-h-0 flex-1 grid-cols-2 gap-3">
          {payload.heatmaps.gex ? (
            <ExposurePanel data={payload.heatmaps.gex} ticker={payload.ticker} />
          ) : null}
          {payload.heatmaps.vex ? (
            <ExposurePanel data={payload.heatmaps.vex} ticker={payload.ticker} />
          ) : null}
        </div>
      </div>
    </main>
  );
}
